/**
 * roofline.ts — Where the trim along a roof's edges goes.
 *
 * A bare pitched slab reads as a wedge of cheese. What turns it into a *roof* is
 * the stuff along its edges: a row of half-round tiles capping the ridge, a
 * rolled arris down each corner of a hip, a barge board following the gable. All
 * of that is arithmetic on the roof's own dimensions, which is exactly the kind
 * of thing that's fiddly in a scene graph and trivial in a test — so it lives
 * here, and `render/buildings.ts` only turns the answers into meshes.
 *
 * Everything is in world units, measured from the roof's own centre at eave
 * level (so y = 0 is the eaves and y = height is the apex or ridge).
 */

/**
 * Centres of a run of equal units filling `length`, in order, centred on 0.
 *
 * Used for ridge tiles. The count is **rounded** and the step derived back from
 * it, so the run always starts and ends a half-unit in from the ends of the
 * ridge — a row that begins flush at one end and dribbles off a third of a tile
 * short at the other is the first thing the eye picks up on a repeated pattern.
 */
export function ridgeTiles(length: number, spacing: number): number[] {
  if (!(length > 0) || !(spacing > 0)) return [0];
  const count = Math.max(1, Math.round(length / spacing));
  const step = length / count;
  const out: number[] = [];
  for (let i = 0; i < count; i++) out.push(-length / 2 + step * (i + 0.5));
  return out;
}

/** One sloping corner of a hipped roof, running from an eave corner to the apex. */
export interface Arris {
  /** Midpoint of the run, as (x, y, z). */
  mid: [number, number, number];
  /** Unit vector pointing from the eave corner up to the apex. */
  dir: [number, number, number];
  /** True length of the run. */
  length: number;
}

/**
 * The four sloping corners of a hipped roof — the lines where two slopes meet.
 *
 * Real hip tiles are rolled over these joints, and without them a hip is four
 * flat triangles sharing an invisible seam: from overhead the whole roof reads
 * as one shape with a crease drawn on it. Each arris runs from a corner of the
 * eave rectangle up to the shared apex.
 *
 * Returned as midpoint + direction because that's what orienting a capsule
 * needs — position it at the middle, then turn its own axis onto `dir`.
 */
export function hipArrises(w: number, d: number, height: number): Arris[] {
  const rise = Math.max(height, 0);
  const hw = w / 2;
  const hd = d / 2;
  const length = Math.hypot(hw, hd, rise);

  return [
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ].map(([sx, sz]) => {
    const cx = sx * hw;
    const cz = sz * hd;
    // Corner → apex. `length` is never 0 for a real roof, but a degenerate
    // footprint shouldn't produce NaNs in a direction vector.
    const inv = length > 0 ? 1 / length : 0;
    return {
      mid: [cx / 2, rise / 2, cz / 2] as [number, number, number],
      dir: [-cx * inv, rise * inv, -cz * inv] as [number, number, number],
      length,
    };
  });
}

/**
 * A point on a pitched slope, `up` of the way from the eave to the ridge.
 *
 * `across` is its horizontal distance from the ridge line (the caller applies
 * the sign for whichever slope it's on) and `y` its height above the eaves.
 * Used to seat rooflights into the tiles; the same arithmetic would seat
 * anything else that has to lie *on* a slope rather than beside it.
 */
export function slopeSeat(span: number, rise: number, up: number): { across: number; y: number } {
  const half = span / 2;
  const t = Math.min(Math.max(up, 0), 1);
  return { across: half * (1 - t), y: rise * t };
}

/**
 * The outward unit normal of a slope that rises `rise` over a horizontal run of
 * `reach`, leaning out along the ground direction (ox, oz).
 *
 * A rooflight lies *in* the tiles, so it needs the surface it's lying on rather
 * than the direction of the roof — pointing one at the slope's uphill direction
 * instead of its normal lays it on edge, standing out of the roof like a fin.
 */
export function slopeNormal(
  ox: number,
  oz: number,
  reach: number,
  rise: number,
): [number, number, number] {
  const len = Math.hypot(reach, rise) || 1;
  return [(ox * rise) / len, reach / len, (oz * rise) / len];
}
